import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import BranchFilter from "@/components/BranchFilter";
import { fmtMoney } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import {
  MagnifyingGlass,
  X,
  CaretLeft,
  CaretRight,
  DownloadSimple,
  Receipt as ReceiptIcon,
  Lightning,
} from "@phosphor-icons/react";

const KIND_META = {
  sale: { label: "Sale", cls: "border-ink bg-canvas-panel text-ink" },
  reconcile: { label: "Reconcile", cls: "border-[#E08600] bg-[#FFF6E5] text-[#A35700]" },
  booking: { label: "Booking", cls: "border-[#5f3dc4] bg-[#5f3dc4]/10 text-[#5f3dc4]" },
  voucher: { label: "Voucher", cls: "border-[#1971c2] bg-[#1971c2]/10 text-[#1971c2]" },
  "voucher:receipt": { label: "V·Receipt", cls: "border-[#1971c2] bg-[#1971c2]/10 text-[#1971c2]" },
  "voucher:payment": { label: "V·Payment", cls: "border-[#1971c2] bg-[#1971c2]/10 text-[#1971c2]" },
  service: { label: "Service", cls: "border-[#00713a] bg-[#00713a]/10 text-[#00713a]" },
  service_advance: { label: "Svc Advance", cls: "border-[#5f3dc4] bg-[#5f3dc4]/10 text-[#5f3dc4]" },
  accessory: { label: "Accessory", cls: "border-[#6f42c1] bg-[#6f42c1]/10 text-[#6f42c1]" },
};

const METHOD_META = {
  cash: { label: "Cash", cls: "border-[#00713a] text-[#00713a]" },
  upi: { label: "UPI", cls: "border-[#5f3dc4] text-[#5f3dc4]" },
  online: { label: "Online", cls: "border-[#1971c2] text-[#1971c2]" },
  card: { label: "Card", cls: "border-[#E08600] text-[#A35700]" },
  platform: { label: "Platform", cls: "border-[#002FA7] text-[#002FA7]" },
};

const KIND_FILTERS = [
  { v: "", l: "All kinds" },
  { v: "sale", l: "Sale" },
  { v: "reconcile", l: "Reconcile" },
  { v: "booking", l: "Booking" },
  { v: "voucher", l: "Voucher" },
  { v: "service", l: "Service" },
  { v: "service_advance", l: "Service Advance" },
  { v: "accessory", l: "Accessory" },
];

const METHOD_FILTERS = [
  { v: "", l: "All methods" },
  { v: "cash", l: "Cash" },
  { v: "upi", l: "UPI" },
  { v: "online", l: "Online" },
  { v: "card", l: "Card" },
  { v: "platform", l: "Platform" },
];

function StatTile({ label, value, testId }) {
  return (
    <div className="border border-canvas-line bg-canvas-card p-4" data-testid={testId}>
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">{label}</div>
      <div className="font-display text-2xl font-bold mt-2 tabular-nums text-ink">{value}</div>
    </div>
  );
}

function KindBadge({ kind }) {
  const meta = KIND_META[kind] || { label: kind, cls: "border-canvas-line text-ink" };
  return (
    <span className={`inline-flex px-1.5 py-0.5 border ${meta.cls} text-[10px] font-mono uppercase tracking-wider`}>
      {meta.label}
    </span>
  );
}

function MethodBadge({ method }) {
  const meta = METHOD_META[method] || { label: method || "—", cls: "border-canvas-line text-ink" };
  return (
    <span className={`inline-flex px-1.5 py-0.5 border ${meta.cls} text-[10px] font-mono uppercase tracking-wider`}>
      {meta.label}
    </span>
  );
}

function toCsv(rows) {
  const headers = [
    "Receipt #",
    "Date",
    "Kind",
    "Branch",
    "Customer",
    "Method",
    "Reference",
    "Amount",
    "Operator",
    "Linked",
  ];
  const esc = (s) => {
    const v = String(s ?? "");
    if (/[",\n]/.test(v)) return `"${v.replace(/"/g, '""')}"`;
    return v;
  };
  const lines = [headers.join(",")];
  for (const r of rows) {
    lines.push(
      [
        r.receipt_no || "",
        r.date || "",
        r.kind || "",
        r.branch_code || "",
        r.customer_name || "",
        r.method || "",
        r.reference || "",
        Number(r.amount || 0).toFixed(2),
        r.operator || "",
        r.source_label || "",
      ].map(esc).join(","),
    );
  }
  return lines.join("\n");
}

const PER_PAGE = 50;

export default function ReceiptsPage() {
  const [filters, setFilters] = useState({
    branch_id: "",
    date_from: "",
    date_to: "",
    kind: "",
    method: "",
    q: "",
  });
  const [page, setPage] = useState(1);
  const [data, setData] = useState({
    rows: [],
    total: 0,
    total_pages: 0,
    totals_by_method: {},
    totals_by_kind: {},
    grand_total: 0,
  });
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const runBackfillPaidOn = async (dryRun) => {
    if (!dryRun && !window.confirm(
      "Stamp paid_on = sale_date on every legacy sale's payment_lines that's missing it?\n\n"
      + "Safe to re-run (idempotent). Required so the Receipts Ledger surfaces all historic sale receipts."
    )) return;
    setBackfilling(true);
    try {
      const { data: r } = await api.post("/sales/admin/backfill-paid-on", null, {
        params: { dry_run: dryRun ? "true" : "false" },
      });
      const verb = dryRun ? "Dry-run" : "Backfill";
      toast.success(
        `${verb} — scanned ${r.scanned} sales, `
        + `${dryRun ? "would patch" : "patched"} ${r.lines_patched} payment_lines `
        + `across ${r.sales_updated} sales.`,
        { duration: 7000 },
      );
      if (!dryRun) load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBackfilling(false);
    }
  };

  const load = useCallback(
    async (overrides = {}) => {
      setLoading(true);
      try {
        const params = { ...filters, ...overrides, page, per_page: PER_PAGE };
        // Strip empty strings so the backend regex validators don't reject them.
        Object.keys(params).forEach((k) => {
          if (params[k] === "" || params[k] === null || params[k] === undefined) delete params[k];
        });
        const { data: d } = await api.get("/receipts/ledger", { params });
        setData(d);
      } catch (err) {
        toast.error(formatApiError(err));
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filters, page],
  );

  useEffect(() => {
    load();
  }, [load]);

  const set = (k) => (v) => {
    setFilters((f) => ({ ...f, [k]: v }));
    setPage(1);
  };

  const clearAll = () => {
    setFilters({ branch_id: "", date_from: "", date_to: "", kind: "", method: "", q: "" });
    setPage(1);
  };

  const onExport = async () => {
    setExporting(true);
    try {
      const params = { ...filters };
      Object.keys(params).forEach((k) => {
        if (params[k] === "" || params[k] === null || params[k] === undefined) delete params[k];
      });
      const { data: d } = await api.get("/receipts/ledger/export", { params });
      const csv = toCsv(d.rows || []);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `receipts-ledger-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Exported ${d.rows.length} rows`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setExporting(false);
    }
  };

  const totalsByMethod = data.totals_by_method || {};
  const totalsByKind = data.totals_by_kind || {};
  const kindOrder = useMemo(
    () => ["sale", "reconcile", "booking", "voucher"].filter((k) => totalsByKind[k]),
    [totalsByKind],
  );

  return (
    <div data-testid="receipts-ledger-page">
      <PageHeader
        eyebrow="06 / Receipts"
        title="Receipts Ledger"
        subtitle="Every receipt issued — sales, reconciles, bookings, vouchers — in one searchable view."
        actions={isAdmin ? (
          <div className="flex items-center gap-2">
            <button
              onClick={() => runBackfillPaidOn(true)}
              disabled={backfilling}
              data-testid="backfill-paid-on-dry-run"
              title="Count how many historic sale payment_lines are missing paid_on. No writes."
              className="border border-canvas-line px-3 py-2.5 text-xs font-mono uppercase tracking-wider text-ink hover:bg-canvas-panel disabled:opacity-50"
            >
              {backfilling ? "Working…" : "Backfill · Dry-run"}
            </button>
            <button
              onClick={() => runBackfillPaidOn(false)}
              disabled={backfilling}
              data-testid="backfill-paid-on-apply"
              title="Stamp paid_on = sale_date on every legacy sale's payment_lines that's missing it. Idempotent."
              className="bg-ink text-white px-3 py-2.5 text-xs font-mono uppercase tracking-wider hover:bg-brand disabled:opacity-50 flex items-center gap-1.5"
            >
              <Lightning size={12} weight="bold" />
              {backfilling ? "Patching…" : "Backfill paid_on"}
            </button>
          </div>
        ) : null}
      />

      <div className="px-8 py-6 space-y-5">
        {/* KPI strip */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <StatTile
            label="Receipts in view"
            value={data.total.toLocaleString("en-IN")}
            testId="receipts-kpi-count"
          />
          <StatTile
            label="Total amount"
            value={fmtMoney(data.grand_total || 0)}
            testId="receipts-kpi-amount"
          />
          {["cash", "upi", "card", "online"].map((m) => (
            <StatTile
              key={m}
              label={METHOD_META[m].label}
              value={fmtMoney(totalsByMethod[m] || 0)}
              testId={`receipts-kpi-${m}`}
            />
          ))}
        </div>

        {/* Kind breakdown */}
        {kindOrder.length > 0 && (
          <div className="flex flex-wrap items-center gap-3 text-xs" data-testid="receipts-kind-rollup">
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">
              By kind:
            </span>
            {kindOrder.map((k) => (
              <div key={k} className="flex items-center gap-1.5">
                <KindBadge kind={k} />
                <span className="font-mono tabular-nums text-ink">{fmtMoney(totalsByKind[k])}</span>
              </div>
            ))}
          </div>
        )}

        {/* Filter bar */}
        <div className="border border-canvas-line bg-canvas-card p-4 space-y-3" data-testid="receipts-filter-bar">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">
                Search
              </label>
              <div className="relative">
                <MagnifyingGlass
                  size={12}
                  weight="bold"
                  className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-subtle"
                />
                <input
                  value={filters.q}
                  onChange={(e) => set("q")(e.target.value)}
                  placeholder="Receipt #, UTR, customer, amount…"
                  data-testid="receipts-search"
                  className="w-full pl-7 pr-7 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
                />
                {filters.q && (
                  <button
                    type="button"
                    onClick={() => set("q")("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-subtle hover:text-ink"
                    aria-label="Clear search"
                  >
                    <X size={11} weight="bold" />
                  </button>
                )}
              </div>
            </div>
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">
                Date from
              </label>
              <input
                type="date"
                value={filters.date_from}
                onChange={(e) => set("date_from")(e.target.value)}
                data-testid="receipts-date-from"
                className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
              />
            </div>
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">
                Date to
              </label>
              <input
                type="date"
                value={filters.date_to}
                onChange={(e) => set("date_to")(e.target.value)}
                data-testid="receipts-date-to"
                className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
              />
            </div>
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">
                Branch
              </label>
              <BranchFilter
                value={filters.branch_id}
                onChange={set("branch_id")}
                testId="receipts-branch-filter"
              />
            </div>
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">
                Kind
              </label>
              <select
                value={filters.kind}
                onChange={(e) => set("kind")(e.target.value)}
                data-testid="receipts-kind-filter"
                className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
              >
                {KIND_FILTERS.map((o) => (
                  <option key={o.v} value={o.v}>{o.l}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-1">
                Method
              </label>
              <select
                value={filters.method}
                onChange={(e) => set("method")(e.target.value)}
                data-testid="receipts-method-filter"
                className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
              >
                {METHOD_FILTERS.map((o) => (
                  <option key={o.v} value={o.v}>{o.l}</option>
                ))}
              </select>
            </div>
            <div className="flex items-end gap-2">
              <button
                type="button"
                onClick={clearAll}
                data-testid="receipts-clear-filters"
                className="px-3 py-2 border border-canvas-line text-sm hover:border-ink"
              >
                Clear
              </button>
              <button
                type="button"
                onClick={onExport}
                disabled={exporting || data.total === 0}
                data-testid="receipts-export-csv"
                className="px-3 py-2 bg-ink text-white text-sm font-semibold hover:bg-brand disabled:opacity-60 flex items-center gap-1.5"
              >
                <DownloadSimple size={12} weight="bold" />
                {exporting ? "Exporting…" : "Export CSV"}
              </button>
            </div>
          </div>
        </div>

        {/* Results table */}
        <div className="border border-canvas-line bg-canvas-card overflow-x-auto">
          <table className="w-full text-sm" data-testid="receipts-table">
            <thead className="bg-canvas-panel border-b border-canvas-line">
              <tr>
                {["Date", "Receipt #", "Kind", "Branch", "Customer", "Method", "Reference", "Amount", "Operator", "Linked"].map((h) => (
                  <th key={h} className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-ink-muted">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && data.rows.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-4 py-10 text-center text-ink-subtle font-mono text-xs">
                    Loading…
                  </td>
                </tr>
              ) : data.rows.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-4 py-12 text-center">
                    <ReceiptIcon size={24} weight="bold" className="text-ink-subtle mx-auto mb-2" />
                    <div className="font-mono text-xs text-ink-subtle">
                      No receipts match the current filters.
                    </div>
                  </td>
                </tr>
              ) : (
                data.rows.map((r, i) => (
                  <tr
                    key={`${r.source_id}-${r.receipt_no || r.reference || i}-${i}`}
                    data-testid={`receipt-row-${i}`}
                    className="border-b border-canvas-line hover:bg-canvas-panel/40"
                  >
                    <td className="px-3 py-2 font-mono text-[11px] text-ink whitespace-nowrap">{r.date || "—"}</td>
                    <td className="px-3 py-2 font-mono text-[11px] text-ink whitespace-nowrap">
                      {r.receipt_no || <span className="text-ink-subtle italic">—</span>}
                    </td>
                    <td className="px-3 py-2"><KindBadge kind={r.kind} /></td>
                    <td className="px-3 py-2 font-mono text-[11px] text-ink-muted">{r.branch_code || "—"}</td>
                    <td className="px-3 py-2 text-ink">{r.customer_name || <span className="text-ink-subtle italic">—</span>}</td>
                    <td className="px-3 py-2"><MethodBadge method={r.method} /></td>
                    <td className="px-3 py-2 font-mono text-[11px] text-ink-muted">
                      {r.reference || <span className="text-ink-subtle italic">—</span>}
                    </td>
                    <td className="px-3 py-2 font-mono tabular-nums text-ink font-semibold">{fmtMoney(r.amount)}</td>
                    <td className="px-3 py-2 text-[12px] text-ink-muted">{r.operator || "—"}</td>
                    <td className="px-3 py-2 font-mono text-[11px] text-ink-muted">{r.source_label || "—"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {data.total > PER_PAGE && (
          <div className="flex items-center justify-between" data-testid="receipts-pagination">
            <div className="font-mono text-xs text-ink-muted">
              Showing {(page - 1) * PER_PAGE + 1}–{Math.min(page * PER_PAGE, data.total)} of {data.total.toLocaleString("en-IN")}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1 || loading}
                data-testid="receipts-page-prev"
                className="p-1.5 border border-canvas-line hover:border-ink disabled:opacity-40"
                aria-label="Previous page"
              >
                <CaretLeft size={12} weight="bold" />
              </button>
              <span className="font-mono text-xs text-ink tabular-nums">
                Page {page} / {data.total_pages || 1}
              </span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(data.total_pages || 1, p + 1))}
                disabled={page >= (data.total_pages || 1) || loading}
                data-testid="receipts-page-next"
                className="p-1.5 border border-canvas-line hover:border-ink disabled:opacity-40"
                aria-label="Next page"
              >
                <CaretRight size={12} weight="bold" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
